import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { parseUserRoles, userHasAnyRole } from '../utils/userRoles';

/**
 * Wraps its children, only rendering them if:
 *  1. The user is logged in
 *  2. Their Firestore `users/{uid}` doc has a `role` string, which may be comma-separated (e.g., "Admin,Staff")
 *     and at least one of those roles is in `allowedRoles` (case-insensitive).
 *
 * Props:
 *  - allowedRoles: array of strings (default ['Admin'])
 *       e.g. ['Admin'], ['Staff'], ['Rider'], or ['Staff','Admin']
 */
export default function ProtectedRoute({ allowedRoles = ['Admin'], children }) {
    const { user } = useAuth();
    const [userRoles, setUserRoles] = useState(null); // null = loading, array = roles, false = no access

    useEffect(() => {
        if (!user) {
            setUserRoles(false);
            return;
        }
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                if (!snap.exists()) {
                    setUserRoles(false);
                } else {
                    setUserRoles(parseUserRoles(snap.data()?.role));
                }
            } catch (err) {
                console.error('ProtectedRoute error fetching user role:', err);
                setUserRoles(false);
            }
        })();
    }, [user]);

    // still loading?
    if (userRoles === null) return null;

    // not logged in?
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // no matching role?
    if (!Array.isArray(userRoles) || !userHasAnyRole(userRoles, allowedRoles)) {
        return <Navigate to="/" replace />;
    }

    // good to go
    return <>{children}</>;
}
